import { Button, Flex, FormItemsGroup, Text } from '@billy/design-system'
import type { GridTemplateValue } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import type { InvoiceFormDepositLine } from '@features/invoices/types/invoiceForm/invoiceFormLine/invoiceFormDepositLine'
import { formatDepositCurrencyAmount } from '@features/invoices/utils/depositInvoices/formatDepositCurrencyAmount'
import { useGetZervantDocumentSettings } from '@features/invoicingSettings/hooks/useGetZervantDocumentSettings'

import { useInvoiceEditorForm } from '../../../../../../../../../../contexts/invoiceEditorFormContext'
import { formatVatRatePercent } from '../../utils/formatVatRatePercent'
import * as Styled from './styles'

interface DepositInvoiceMultiVatLinesProps {
  columnItemsTemplate: GridTemplateValue[]
  depositLines: InvoiceFormDepositLine[]
  onEditClick: () => void
}

export const DepositInvoiceMultiVatLines = ({
  columnItemsTemplate,
  depositLines,
  onEditClick,
}: DepositInvoiceMultiVatLinesProps): ReactElement => {
  const { t } = useTranslation()
  const { invoiceEditorForm } = useInvoiceEditorForm()
  const { getInvoiceLocale } = useGetZervantDocumentSettings(InvoiceDocumentType.Invoice)

  const { watch } = invoiceEditorForm
  const currency = watch('currency')
  const locale = getInvoiceLocale()

  return (
    <>
      <Styled.DepositInvoiceMultiVatDescriptionRowWrapper>
        <FormItemsGroup itemsInRow={columnItemsTemplate.length} itemsTemplate={columnItemsTemplate}>
          <Text>{depositLines[0]?.description}</Text>
          <Styled.EmptyGridCell />
          <Styled.EmptyGridCell />
          <Styled.DepositInvoiceMultiVatEditButtonCell>
            <Flex alignItems="center" justifyContent="flex-end">
              <Button onClick={onEditClick} icon="squarePen" size="sm" variant="text">
                {t('external_invoices.editor.deposit_lines.edit_button')}
              </Button>
            </Flex>
          </Styled.DepositInvoiceMultiVatEditButtonCell>
          <Styled.EmptyGridCell />
        </FormItemsGroup>
      </Styled.DepositInvoiceMultiVatDescriptionRowWrapper>
      {depositLines.map((line, index) => {
        const vatRatePercentDisplay = formatVatRatePercent(line.vatRatePercent, locale)

        return (
          <FormItemsGroup
            itemsInRow={columnItemsTemplate.length}
            itemsTemplate={columnItemsTemplate}
            key={line.vatRateId ?? index}
          >
            <Styled.DepositInvoiceMultiVatSplitLabel>
              <Text>
                {t('external_invoices.editor.deposit_lines.vat_split_label', { vatRate: vatRatePercentDisplay })}
              </Text>
            </Styled.DepositInvoiceMultiVatSplitLabel>
            <Text alignment="right">{formatDepositCurrencyAmount(line.totalExcludingVat ?? 0, currency, locale)}</Text>
            <Text alignment="right">{vatRatePercentDisplay}</Text>
            <Text alignment="right">{formatDepositCurrencyAmount(line.totalIncludingVat ?? 0, currency, locale)}</Text>
            <Styled.EmptyGridCell />
          </FormItemsGroup>
        )
      })}
    </>
  )
}
